import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import { AutocompleteField } from "../../shared/inputs/AutocompleteField";
import { PrimaryButton } from "../../shared/buttons/PrimaryButton.styled";
import { SecondaryButton } from "../../shared/buttons/SecondaryButton.styled";
import { BodyText } from "../../shared/text/BodyText.styled";
import { ediphiPalette } from "../../styles/colors";
import { getEstimateReportData } from "../../../excel/workbook";
import { getProcessedEstimate } from "../../../excel/datasources/estimate";
import type { ProcoreCostTypeManifold, ProcoreExportOptions } from "../../../integrations/exports/procore";
import {
  PROCORE_MARKUP_REUSE_COST_TYPE,
  PROCORE_MARKUP_REUSE_OTHER,
  PROCORE_SORT_FIELDS_TITLE,
  PROCORE_TIER_MANIFOLD_LABEL,
  PROCORE_TIER_SINGLE_LABEL,
} from "../../constants";
import { browserStore } from "../../../utils/storageKeys";

const STORAGE_KEY = "procoreExportSortFields";

type Tier = "single" | "manifold";

interface ManifoldSelections {
  labor: string | null;
  material: string | null;
  equip: string | null;
  sub: string | null;
  other: string | null;
}

interface StoredSelections {
  tier?: Tier;
  costCodeSortField?: string;
  costTypeSortField?: string;
  manifold?: Partial<ManifoldSelections>;
  markupCostTypeSortField?: string;
}

const EMPTY_MANIFOLD: ManifoldSelections = { labor: null, material: null, equip: null, sub: null, other: null };

const MANIFOLD_LABELS: { key: keyof ManifoldSelections; label: string }[] = [
  { key: "labor", label: "Labor Cost Type sort field" },
  { key: "material", label: "Material Cost Type sort field" },
  { key: "equip", label: "Equipment Cost Type sort field" },
  { key: "sub", label: "Subcontract Cost Type sort field" },
  { key: "other", label: "Other Cost Type sort field" },
];

const loadStoredSelections = (): StoredSelections => {
  try {
    const raw = browserStore.get(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredSelections) : {};
  } catch {
    return {};
  }
};

interface ProcoreExportDialogProps {
  onSubmit: (opts: ProcoreExportOptions) => void;
  onClose: () => void;
  onLoadFailed: () => void;
}

/**
 * Picks which sort fields carry the Procore Cost Code and Cost Type. Owns its own MUI dialog rather
 * than going through `useDialog`, which holds a static node and so cannot host a form.
 *
 * Mounted only while open, so the effect below runs once per opening.
 */
const ProcoreExportDialog: React.FC<ProcoreExportDialogProps> = ({ onSubmit, onClose, onLoadFailed }) => {
  const [sortFieldNames, setSortFieldNames] = useState<string[]>([]);
  const [tier, setTier] = useState<Tier>("single");
  const [costCodeSortField, setCostCodeSortField] = useState<string | null>(null);
  const [costTypeSortField, setCostTypeSortField] = useState<string | null>(null);
  const [manifold, setManifold] = useState<ManifoldSelections>(EMPTY_MANIFOLD);
  const [markupCostTypeSortField, setMarkupCostTypeSortField] = useState<string | null>(null);
  // Tracked separately from `sortFieldNames.length`: an estimate whose line items carry no sort
  // field values loads successfully with none, and the pickers must say "No options", not spin.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const stored = loadStoredSelections();
    (async () => {
      try {
        const reportData = await getEstimateReportData();
        const estimateId = reportData[0]?.estimate_id;
        if (cancelled) return;
        if (!estimateId) {
          onLoadFailed();
          return;
        }
        const processed = await getProcessedEstimate(estimateId as string);
        if (cancelled) return;
        const names = Array.from(
          new Set(
            Object.values(processed?.usedSortFields ?? {})
              .map((sf) => sf.name)
              .filter(Boolean)
          )
        ).sort();
        setSortFieldNames(names);
        setTier(stored.tier ?? "single");
        setCostCodeSortField(stored.costCodeSortField ?? null);
        setCostTypeSortField(stored.costTypeSortField ?? null);
        setManifold({ ...EMPTY_MANIFOLD, ...stored.manifold });
        setMarkupCostTypeSortField(stored.markupCostTypeSortField ?? null);
      } catch {
        if (!cancelled) onLoadFailed();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const manifoldComplete =
    !!manifold.labor && !!manifold.material && !!manifold.equip && !!manifold.sub && !!manifold.other;
  const submitDisabled = !costCodeSortField || (tier === "single" ? !costTypeSortField : !manifoldComplete);

  const handleSubmit = () => {
    if (!costCodeSortField) return;
    const costTypeConfig: ProcoreExportOptions["costTypeSortField"] =
      tier === "single" ? (costTypeSortField as string) : (manifold as ProcoreCostTypeManifold);

    const stored: StoredSelections = {
      tier,
      costCodeSortField,
      ...(tier === "single" ? { costTypeSortField: costTypeSortField ?? undefined } : { manifold }),
      ...(markupCostTypeSortField ? { markupCostTypeSortField } : {}),
    };
    browserStore.set(STORAGE_KEY, JSON.stringify(stored));

    onSubmit({
      costCodeSortField,
      costTypeSortField: costTypeConfig,
      ...(markupCostTypeSortField ? { markupCostTypeSortField } : {}),
    });
  };

  const sortFieldPicker = (label: string, value: string | null, onChange: (next: string | null) => void) => (
    <AutocompleteField
      options={sortFieldNames}
      value={value}
      onChange={onChange}
      getOptionLabel={(name) => name}
      getOptionId={(name) => name}
      label={label}
      loading={loading}
    />
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth sx={{ m: 2 }}>
      <DialogTitle sx={{ color: ediphiPalette.black950, fontSize: "16px", fontWeight: 700, lineHeight: "24px" }}>
        {PROCORE_SORT_FIELDS_TITLE}
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: 2 }}>
        <BodyText sx={{ mb: 2 }}>
          Choose which sort fields hold the Procore Cost Code and Cost Type for this estimate.
        </BodyText>
        <RadioGroup row value={tier} onChange={(event) => setTier(event.target.value as Tier)} sx={{ mb: 1 }}>
          <FormControlLabel value="single" control={<Radio size="small" />} label={PROCORE_TIER_SINGLE_LABEL} />
          <FormControlLabel value="manifold" control={<Radio size="small" />} label={PROCORE_TIER_MANIFOLD_LABEL} />
        </RadioGroup>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {sortFieldPicker("Cost Code sort field", costCodeSortField, setCostCodeSortField)}
          {tier === "single"
            ? sortFieldPicker("Cost Type sort field", costTypeSortField, setCostTypeSortField)
            : MANIFOLD_LABELS.map(({ key, label }) => (
                <Box key={key}>
                  {sortFieldPicker(label, manifold[key], (next) => setManifold((prev) => ({ ...prev, [key]: next })))}
                </Box>
              ))}
          {sortFieldPicker(
            "Markup Cost Type sort field (optional)",
            markupCostTypeSortField,
            setMarkupCostTypeSortField
          )}
          <BodyText>{tier === "single" ? PROCORE_MARKUP_REUSE_COST_TYPE : PROCORE_MARKUP_REUSE_OTHER}</BodyText>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: "0 24px 24px 24px" }}>
        <SecondaryButton size="large" autoFocus onClick={onClose}>
          Cancel
        </SecondaryButton>
        <PrimaryButton size="large" onClick={handleSubmit} disabled={submitDisabled}>
          Create
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default ProcoreExportDialog;
